import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService, UserRole } from '@nebalkon-workspace/auth';
import {
  WalletAdminService,
  type WalletStatsResponse,
} from '@nebalkon-workspace/admin-api';

@Injectable({ providedIn: 'root' })
export class StaffWalletStatsService {
  readonly #api = inject(WalletAdminService);
  readonly #auth = inject(AuthService);

  readonly #stats = signal<WalletStatsResponse | null>(null);
  readonly #loading = signal(false);
  readonly #loaded = signal(false);
  readonly #accessDenied = signal(false);
  readonly #error = signal(false);

  readonly stats = this.#stats.asReadonly();
  readonly loading = this.#loading.asReadonly();
  readonly accessDenied = this.#accessDenied.asReadonly();
  readonly error = this.#error.asReadonly();

  readonly #canLoad = computed(() => {
    const roles = this.#auth.roles();
    return (
      roles.includes(UserRole.Admin) || roles.includes(UserRole.Manager)
    );
  });

  readonly debtorsCount = computed(() => {
    if (!this.#canLoad()) {
      return 0;
    }

    return this.#stats()?.debtors_count ?? 0;
  });

  ensureLoaded(): void {
    if (!this.#canLoad() || this.#loaded() || this.#loading()) {
      return;
    }

    this.refresh();
  }

  refresh(): void {
    if (!this.#canLoad()) {
      return;
    }

    this.#loading.set(true);
    this.#accessDenied.set(false);
    this.#error.set(false);

    this.#api.getStats().subscribe({
      next: (stats) => {
        this.#stats.set(stats);
        this.#loaded.set(true);
        this.#loading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.#loading.set(false);
        this.#loaded.set(true);
        if (err.status === 403) {
          this.#accessDenied.set(true);
        } else {
          this.#error.set(true);
        }
      },
    });
  }
}
